/**
 * The Clubhouse's unread model — pure, so it can be tested without a DOM, and
 * separate from the storage that feeds it (the presence rail's split between
 * `presence/status.ts` and the component that renders it).
 *
 * Unread is LOCAL and per-browser: a `localStorage` watermark, no server state,
 * no read receipts, nothing to migrate. That is deliberate for a room whose
 * messages expire in a day — knowing exactly who has seen what is a heavier
 * promise than "say hi" needs, and it would leak who is lurking.
 */
import type { ChatMessage } from '@djgg/creations';

/** Where the watermark lives. */
export const CHAT_LAST_READ_KEY = 'djgg.chat.lastReadAt';

/**
 * How many messages are newer than `lastReadAt`.
 *
 * Your OWN messages never count: you have obviously seen them, and a badge that
 * ticked up when you pressed Send would read as a bug.
 */
export function unreadCount(
  rows: readonly ChatMessage[],
  lastReadAt: number,
  myUid: string,
): number {
  return rows.filter((row) => row.uid !== myUid && row.createdAt > lastReadAt).length;
}

/** The newest `createdAt` in `rows`, or 0 for an empty room. */
export function newestAt(rows: readonly ChatMessage[]): number {
  return rows.reduce((max, row) => (row.createdAt > max ? row.createdAt : max), 0);
}

/** Read the watermark. A browser with no entry (or a wiped one) starts at 0 —
 *  the first visit shows everything in the window as unread, which is right:
 *  it IS all new to them. */
export function readWatermark(): number {
  const raw = localStorage.getItem(CHAT_LAST_READ_KEY);
  const parsed = raw === null ? 0 : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/** Advance the watermark. Never moves BACKWARDS — two tabs open on the same
 *  room would otherwise fight, and the stale one would resurrect old unreads. */
export function writeWatermark(at: number): void {
  if (at > readWatermark()) localStorage.setItem(CHAT_LAST_READ_KEY, String(at));
}
